import { reactive } from "vue";

export interface PickupDraftLineItem {
  productSlug: string;
  quantity: number;
}

export interface PickupDraft {
  agbConsent: boolean;
  categorySlug: string;
  customerEmail: string;
  customerName: string;
  customerPhoneNumber: string;
  lineItems: PickupDraftLineItem[];
  newsletterOptIn: boolean;
  pickupAt: string;
}

const SESSION_STORAGE_KEY = "termine:pickup-draft";

const emptyDraft: PickupDraft = {
  agbConsent: false,
  categorySlug: "",
  customerEmail: "",
  customerName: "",
  customerPhoneNumber: "",
  lineItems: [],
  newsletterOptIn: false,
  pickupAt: "",
};

function _loadFromSessionStorage(): PickupDraft {
  if (typeof window === "undefined") {
    return {
      ...emptyDraft,
    };
  }
  const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) {
    return {
      ...emptyDraft,
    };
  }
  try {
    const parsed = JSON.parse(raw);
    return {
      ...emptyDraft,
      ...parsed,
    };
  } catch {
    return {
      ...emptyDraft,
    };
  }
}

const draft = reactive<PickupDraft>(_loadFromSessionStorage());

function _persistDraft() {
  if (typeof window === "undefined") {
    return;
  }
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(draft));
}

export function usePickupDraft() {
  return {
    addLineItem(productSlug: string, quantity: number) {
      const existing = draft.lineItems.find((item) => item.productSlug === productSlug);
      if (existing) {
        existing.quantity = quantity;
      } else {
        draft.lineItems.push({
          productSlug,
          quantity,
        });
      }
      _persistDraft();
    },
    clear() {
      Object.assign(draft, {
        ...emptyDraft,
        lineItems: [],
      });
      _persistDraft();
    },
    draft,
    persist: _persistDraft,
    removeLineItem(productSlug: string) {
      draft.lineItems = draft.lineItems.filter((item) => item.productSlug !== productSlug);
      _persistDraft();
    },
    update(patch: Partial<PickupDraft>) {
      Object.assign(draft, patch);
      _persistDraft();
    },
  };
}
